/**
 * @description 季报用户偏好组件
 * @date 2025-01-24
 * @author lv.chen
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import Image from '@user-center/components/Image';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { getGameEntityTypeClassName } from '../../utils';
import { GENRE_NAME_MAP, ENTITY_TYPE_NAME_MAP, getYearConfig } from '../../constants';

interface IUserPreferProps extends IBaseProps {
  year?: number;
  favouriteGames?: IQuarterReportTopFavouriteGame[] | null;
  favouriteGenre?: string;
  favouriteEntityType?: string;
}

const defaultFavouriteGames: IQuarterReportTopFavouriteGame[] = [];

const calcUserPreferClassName = (params?: {
  favouriteGames?: IQuarterReportTopFavouriteGame[] | null;
  favouriteGenre?: string;
  favouriteEntityType?: string;
}) => {
  const { favouriteGames, favouriteGenre, favouriteEntityType } = params || {};
  const flags = [!!favouriteGames?.length, !!favouriteGenre, !!favouriteEntityType].filter(Boolean);
  if (flags.length === 3) {
    return '';
  }

  if (flags.length === 2) {
    return 'user-prefer02';
  }

  if (flags.length === 1) {
    return 'user-prefer01';
  }
};

/**
 * 季报用户偏好组件
 */
const UserPrefer: React.FC<IUserPreferProps> = (props) => {
  const {
    style,
    className,
    year,
    favouriteGames = defaultFavouriteGames,
    favouriteGenre = '',
    favouriteEntityType = '',
  } = props;

  const { formatMessage } = useIntlExtend();

  const resolvedYear = year ?? moment().year();
  const yearConfig = useMemo(() => getYearConfig(resolvedYear), [resolvedYear]);

  const userPreferClassName = calcUserPreferClassName({ favouriteGames, favouriteGenre, favouriteEntityType });

  // 获取品类名
  const genreName = GENRE_NAME_MAP[favouriteGenre] || '';
  // 获取品类图标
  const genreIcon = yearConfig.GENRE_ICON_MAP[favouriteGenre] || '';

  return (
    // 无最爱游戏top3 增加class user-prefer02
    // 单个情况 user-prefer01
    <div
      style={style}
      className={classnames('user-prefer', className, userPreferClassName, {
        'user-prefer-no-games': !favouriteGames?.length,
      })}
    >
      <div className="quarter-report-title">
        <span>{formatMessage({ id: 'dbu_quarter_report_game_preference_profile' })}</span>
      </div>
      <div className="user-prefer-content">
        <div className="user-prefer-list">
          {!!favouriteGames?.length && (
            <div className="user-prefer-item --top3">
              <div className="user-prefer-item-title">
                <span>
                  {formatMessage({ id: 'dbu_quarter_report_favourite_game_top3' })}
                  <span className="--text">{formatMessage({ id: 'dbu_quarter_report_top3_label' })}</span>
                </span>
              </div>
              <div className="user-prefer-item-content">
                <div className="user-prefer-game-list">
                  {favouriteGames?.slice(0, 3)?.map((game, index) => (
                    <div key={index} className="user-prefer-game-item">
                      <div
                        className={classnames(
                          'user-prefer-game-item-pic',
                          getGameEntityTypeClassName(game?.entity_type),
                        )}
                      >
                        <Image src={game?.cover || ''} />
                      </div>
                      <div className="user-prefer-game-item-title">{game?.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {!!favouriteGenre && (
            <div className="user-prefer-item --type">
              <div className="user-prefer-item-title">
                <span>{formatMessage({ id: 'dbu_quarter_report_favourite_genre' })}</span>
              </div>
              <div className="user-prefer-item-desc">
                <span>[ {formatMessage({ id: genreName })} ]</span>
              </div>
              <div className="user-prefer-item-pic">
                <Image src={genreIcon} />
              </div>
            </div>
          )}
          {!!favouriteEntityType && (
            <div className="user-prefer-item --visit">
              <div className="user-prefer-item-title">
                <span>{formatMessage({ id: 'dbu_quarter_report_favourite_entity_type' })}</span>
              </div>
              <div className="user-prefer-item-desc">
                <span>[ {formatMessage({ id: ENTITY_TYPE_NAME_MAP[favouriteEntityType] })} ]</span>
              </div>
              <div className={classnames('user-prefer-item-pic', getGameEntityTypeClassName(favouriteEntityType))}>
                <Image src={yearConfig.ENTITY_TYPE_ICON_MAP[favouriteEntityType]} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPrefer;
